import { useEffect, useRef, useState } from "react";
import { STATS, STATIC } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const Counter = ({ value, prefix = "", suffix = "", decimals }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(STATIC ? value : 0);
  const dp = decimals ?? (value % 1 !== 0 ? 1 : 0);

  useEffect(() => {
    if (STATIC) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        const duration = 1600;
        const start = performance.now();
        const tick = () => {
          const p = Math.min(1, (performance.now() - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setDisplay(value * eased);
          if (p < 1) setTimeout(tick, 32);
          else setDisplay(value);
        };
        tick();
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value]);

  return (
    <span ref={ref}>
      {prefix}
      {display.toFixed(dp)}
      {suffix}
    </span>
  );
};

export const Stats = () => {
  return (
    <section
      id="results"
      data-testid="stats-section"
      className="relative bg-[#0a0a0a] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal
          as="p"
          y={20}
          className="mb-14 font-mono-accent text-xs uppercase tracking-[0.3em] text-yellow-400"
        >
          / The numbers don't lie
        </Reveal>

        <div className="grid grid-cols-2 gap-x-6 gap-y-12 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal
              key={s.label}
              delay={i * 100}
              data-testid={`stat-${i}`}
              className="border-t border-white/10 pt-5"
            >
              <div className="font-display text-5xl text-yellow-400 sm:text-7xl">
                <Counter
                  value={s.value}
                  prefix={s.prefix || ""}
                  suffix={s.suffix || ""}
                  decimals={s.decimals}
                />
              </div>
              <p className="mt-3 text-sm text-neutral-400">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
